import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, MonitorSmartphone, FileDown } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { CheckResults } from '@/components/spec/CheckResults';
import { checkReceipt } from '@/lib/consentChecker';
import vectors from '@/lib/openConsentVectors.json';

const VECTORS_URL = '/downloads/kojiru-open-consent-receipt-test-vectors-draft-0.1.json';
const pretty = (o) => JSON.stringify(o, null, 2);

function Field({ label, value, onChange, rows, testid, hint }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-gray-400">{label}</span>
      {hint && <span className="block text-[11px] text-gray-500 mt-1">{hint}</span>}
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} spellCheck={false} className="mt-2 w-full rounded-xl border border-white/10 bg-[#0a0a0a] p-3 font-mono text-xs text-gray-200 focus:border-sky-500/60 focus:outline-none" data-testid={testid} />
    </label>
  );
}

function ExamplePicker({ onLoad }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="checker-examples">
      <span className="text-xs text-gray-500 mr-1">Load example:</span>
      {vectors.cases.map((c) => (
        <button key={c.id} type="button" onClick={() => onLoad(c)} className="rounded-full border border-white/15 px-3 py-1 text-xs text-gray-200 hover:border-sky-400/60 hover:text-white transition-colors" data-testid={`checker-load-${c.id}`}>{c.id}</button>
      ))}
    </div>
  );
}

export default function ConsentReceiptChecker() {
  const [receipt, setReceipt] = useState('');
  const [doc, setDoc] = useState('');
  const [ctx, setCtx] = useState('');
  const [chainId, setChainId] = useState(String(vectors.domain.chainId));
  const [expected, setExpected] = useState(null);
  const [result, setResult] = useState(null);
  const load = (c) => { setChainId(String(vectors.domain.chainId)); setReceipt(pretty(c.receipt)); setDoc(c.documentText); setCtx(pretty(c.context)); setExpected(c.expected); setResult(null); };
  const run = async () => {
    try {
      const spec = { ...vectors, domain: { ...vectors.domain, chainId: Number(chainId) } };
      const r = await checkReceipt(spec, { receipt: JSON.parse(receipt), documentText: doc, context: ctx.trim() ? JSON.parse(ctx) : {} });
      setResult(expected ? { ...r, expected, matches: r.valid === expected.valid && r.failedRule === expected.failedRule } : r);
    } catch (e) {
      setResult({ error: `Could not check: ${e.message}` });
    }
  };
  const edit = (setter) => (v) => { setter(v); setExpected(null); };
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="consent-checker-page">
      <Helmet><title>Consent Receipt Checker | Open Consent Receipt Specification | Kojiru</title></Helmet>
      <Navbar />
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <Link to="/specs/consent-receipt" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors mb-8" data-testid="checker-back-link"><ArrowLeft className="w-3.5 h-3.5" /> Open Consent Receipt Specification</Link>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4" data-testid="checker-title">Consent receipt checker</h1>
          <p className="text-base text-gray-400 max-w-3xl leading-relaxed mb-6">Paste a receipt, its document, and any context. Your browser runs the seven rules from section 6 of the spec.</p>
          <div className="rounded-2xl border border-sky-500/30 bg-sky-500/[0.05] p-5 flex gap-3 mb-8 max-w-3xl" data-testid="checker-local-notice">
            <MonitorSmartphone className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
            <p className="text-sm text-sky-100/80 leading-relaxed">The check runs on your device. Nothing you paste is sent to Kojiru or stored. The result shows only whether signatures and hashes match. It says nothing about legal effect and is not a determination by Kojiru. Provided as is, without warranty.</p>
          </div>
          <ExamplePicker onLoad={load} />
          <a href={VECTORS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-sky-300 hover:text-sky-200 mt-3" data-testid="checker-vectors-download"><FileDown className="w-3.5 h-3.5" /> Download all test vectors (JSON)</a>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <div className="space-y-5">
              <Field label="Receipt" hint='JSON with "message" and "signature"' value={receipt} onChange={edit(setReceipt)} rows={12} testid="checker-receipt-input" />
              <Field label="Document text" hint="Exact text the receipt covers" value={doc} onChange={edit(setDoc)} rows={4} testid="checker-document-input" />
              <Field label="Context (optional)" hint="checkTime, declaredClass, usedNonces, revocations, operator, allowlist" value={ctx} onChange={edit(setCtx)} rows={8} testid="checker-context-input" />
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-gray-400">Domain chain id</span>
                <input value={chainId} onChange={(e) => edit(setChainId)(e.target.value.replace(/\D/g, ''))} className="mt-2 w-40 rounded-xl border border-white/10 bg-[#0a0a0a] p-2.5 font-mono text-xs text-gray-200 focus:border-sky-500/60 focus:outline-none" data-testid="checker-chain-input" />
              </label>
              <button type="button" onClick={run} disabled={!receipt.trim()} className="rounded-full bg-white text-black px-6 py-2.5 text-sm font-semibold hover:bg-gray-200 transition-colors disabled:opacity-40" data-testid="checker-run-button">Check in my browser</button>
            </div>
            <div><CheckResults result={result} /></div>
          </div>
          <p className="text-[11px] text-gray-500 mt-10 max-w-3xl" data-testid="checker-domain-note">Checks use the spec domain: name "{vectors.domain.name}", version "{vectors.domain.version}", and the chain id you enter (the test vectors use {vectors.domain.chainId}). Receipts signed for another domain will fail the Signer rule.</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
